/* global Theme */
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { cartSignature, isGiftLine, sectionCandidates } from '@bbd/cart-rules';

/**
 * Cart sections behaviour (PIG-320).
 *
 * Open-section controller: one section open at a time (the details share a name). It only switches on a real
 * transition, a plain quantity change or removal leaves the sections as they are:
 * - a gift was added: the giftbox section;
 * - the threshold was just reached, no gift yet and no "Geen" chosen: the gift section;
 * - "Geen" was chosen: "Maak je look compleet";
 * - add to cart: the gift section when it is unlocked and empty, otherwise "Maak je look compleet".
 * A drawer opened with the cart icon starts with all sections closed.
 * Open state survives the morph of the cart section, morph.js keeps `open` on details.
 *
 * Everything is delegated to document, so it keeps working when the cart section is morphed.
 */
const GIFT_SYNCED_EVENT = 'bbd-gift:synced';
const DISMISSED_KEY = 'bbd_gwp_dismissed';

/** How long we wait for the gift picker before we decide without it */
const GIFT_PICKER_TIMEOUT = 2000;

/** Time for the morph of the cart section to finish before the open lists are checked */
const LIST_REPORT_DELAY = 1000;

/** The drawer opens on the add to cart event, within this time that open is an add and not the cart icon */
const ADD_TO_OPEN_WINDOW = 1000;

const SECTIONS = {
  look: '.cart-crosssells > .details',
  gift: '.cart-gwp:not(.cart-crosssells) > .details',
  giftbox: '.cart-giftbox > .details'
};

/** Cart changes that come from the section itself: the customer is working in it, keep it open */
const KEEP_OPEN_SOURCES = new Set(['bbd-giftbox-picker']);

let addedAt = 0;
/** @type {{ source?: string, isAdd: boolean, timer: ReturnType<typeof setTimeout> } | null} */
let pending = null;
/** @type {string | null | undefined} */
let lastGiftKey;
/** @type {string | null} */
let lastSignature = null;
/** @type {boolean | undefined} */
let lastLocked;

/**
 * @param {keyof typeof SECTIONS} name
 * @returns {HTMLDetailsElement[]}
 */
const getSections = (name) =>
  [...document.querySelectorAll(SECTIONS[name])].filter((element) => element instanceof HTMLDetailsElement);

const closeAll = () => {
  for (const name of /** @type {(keyof typeof SECTIONS)[]} */ (Object.keys(SECTIONS))) {
    for (const details of getSections(name)) details.open = false;
  }
};

/**
 * @param {keyof typeof SECTIONS} name
 * @param {{ scroll?: boolean }} [options]
 */
const openSection = (name, { scroll = false } = {}) => {
  const sections = getSections(name);
  if (sections.length === 0) return false;

  closeAll();
  for (const details of sections) {
    details.open = true;
    if (!scroll) continue;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    requestAnimationFrame(() => details.scrollIntoView({ behavior, block: 'nearest' }));
  }

  return true;
};

const isDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * @param {{ locked: boolean, giftKey: string | null, cartSignature: string | null } | null} state - The gift picker state after the cart changed, null when there is no picker
 */
const settle = async (state) => {
  if (!pending) return;
  const { source, isAdd } = pending;
  clearTimeout(pending.timer);
  pending = null;

  const locked = state?.locked ?? true;
  let giftKey = state?.giftKey ?? null;
  let signature = state?.cartSignature ?? null;

  // No gift picker: read the cart ourselves, so a background refresh without changes is still recognised
  if (!state) {
    try {
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' })).json();
      giftKey = cart.items.find(isGiftLine)?.key ?? null;
      signature = cartSignature(cart.items);
    } catch {
      return;
    }
  }

  const cartChanged = signature !== lastSignature;
  const giftAdded = giftKey !== null && giftKey !== lastGiftKey;
  const giftRemoved = giftKey === null && lastGiftKey != null;
  const thresholdReached = lastLocked === true && !locked;
  lastSignature = signature;
  lastGiftKey = giftKey;
  lastLocked = locked;

  // The first candidate that exists on the page is opened (a store can switch the giftbox section off)
  const candidates = sectionCandidates({
    cartChanged,
    keepOpen: Boolean(source && KEEP_OPEN_SOURCES.has(source)),
    isAdd,
    giftAdded,
    giftRemoved,
    thresholdReached,
    locked,
    hasGift: giftKey !== null,
    dismissed: isDismissed()
  });

  for (const name of candidates) {
    if (openSection(name, { scroll: name === 'gift' })) return;
  }
};

// CartAddEvent has the same event name as CartUpdateEvent, an add to cart is told apart by its class
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.data?.didError) return;

  const isAdd = Boolean(pending?.isAdd) || event instanceof CartAddEvent;
  if (pending) clearTimeout(pending.timer);
  // Without a gift picker (a voordeelset is in the cart) no sync will come, so there is nothing to wait for
  const wait = document.querySelector('bbd-gift-picker') ? GIFT_PICKER_TIMEOUT : 0;
  pending = { source: detail?.data?.source, isAdd, timer: setTimeout(() => settle(null), wait) };

  if (event instanceof CartAddEvent) addedAt = Date.now();
});

document.addEventListener(GIFT_SYNCED_EVENT, (event) => {
  const state = /** @type {CustomEvent} */ (event).detail;

  if (pending) {
    settle(state);
    return;
  }

  // A sync without a cart change in front of it (page load): just remember where we are
  lastSignature = state.cartSignature;
  lastGiftKey = state.giftKey;
  lastLocked = state.locked;
});

// The drawer opened by the cart icon starts closed, an add to cart open keeps what the cart rules choose.
// Capture: the dialog event does not bubble. Timeout: the add to cart listener of the drawer may run after ours.
document.addEventListener(
  'dialog:open',
  (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('cart-drawer-component')) return;

    setTimeout(() => {
      if (Date.now() - addedAt < ADD_TO_OPEN_WINDOW) return;
      closeAll();
    }, 0);
  },
  true
);

/**
 * GA4 view_item_list for the lists in the sections (Mix & match, giftboxes): they are reported when their section is
 * open, not when the page loads. The lists carry `data-datalayer-deferred` so the ecommerce script leaves them alone.
 * A list is reported once per render: a cart refresh that renders new elements reports it again.
 * @type {WeakSet<Element>}
 */
const reportedLists = new WeakSet();

/**
 * @param {Element} section
 */
const reportLists = (section) => {
  for (const container of section.querySelectorAll('[data-datalayer-deferred]')) {
    if (reportedLists.has(container) || !container.querySelector('[data-datalayer-item]')) continue;

    reportedLists.add(container);
    container.dispatchEvent(new CustomEvent('datalayer:list-ready', { bubbles: true, detail: { container } }));
  }
};

const reportOpenLists = () => {
  for (const details of document.querySelectorAll('.bbd-cart-sections details[open]')) reportLists(details);
};

// The toggle event does not bubble, listen in the capture phase
document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement && event.target.open && event.target.closest('.bbd-cart-sections')) {
      reportLists(event.target);
    }
  },
  true
);

// After a cart refresh the open section may hold new elements, give the morph time to finish
/** @type {ReturnType<typeof setTimeout> | undefined} */
let reportTimer;
document.addEventListener(ThemeEvents.cartUpdate, () => {
  clearTimeout(reportTimer);
  reportTimer = setTimeout(reportOpenLists, LIST_REPORT_DELAY);
});

// The cart page has no cart icon that opens it closed: it starts with "Maak je look compleet" open, like an add to cart
// in the drawer does. #bbd-datalayer-cart is only on the cart page (sections/main-cart.liquid).
const openDefaultSectionOnCartPage = () => {
  if (document.getElementById('bbd-datalayer-cart')) openSection('look');
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', openDefaultSectionOnCartPage);
} else {
  openDefaultSectionOnCartPage();
}

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-cart-gift-edit]') : null;
  if (!trigger) return;

  openSection('gift', { scroll: true });
});
